import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useSubmitGetInTouchDetailsMutation } from 'generated/graphql';
import { appFormUtil } from 'modules';
import {
  useCallback,
  useMemo,
  useState
} from 'react';

import imgCustomizeCapabilities from 'assets/customizeCapabilities.svg';
import imgCustomizeCopy from 'assets/customizeCopy.svg';



const LOCAL_STORAGE_KEY = 'didSubmitForm';



export type CustomizeActivityProps = {};

export const CustomizeActivity = (props: CustomizeActivityProps) => {
  const [
    didSubmit,
    setDidSubmit
  ] = useState(localStorage.getItem(LOCAL_STORAGE_KEY) === 'true');

  const [
    submitGetInTouchDetails,
    submitGetInTouchDetailsTuple
  ] = useSubmitGetInTouchDetailsMutation({
  });

  const [
    showForm,
    setShowForm
  ] = useState(false);

  const {
    formState,
    genFieldProps
  } = appFormUtil.useFormState({
    initialState: {
      email: '',
      name: '',
      companyName: '',
      assetsUnderManagement: '',
      otherComments: ''
    },
    key2FieldConfig: {
      email: {
        label: 'Email',
        required: true,
        type: 'email'
      },
      name: {
        label: 'Name',
        required: true
      },
      companyName: {
        label: 'Company Name',
        required: true
      },
      assetsUnderManagement: {
        label: 'Assets under management ($)',
        required: false
      },
      otherComments: {
        label: 'Other Comments',
        required: false
      }
    }
  });



  const handleClickSubmit = useCallback(async () => {
    await submitGetInTouchDetails({
      variables: {
        input: formState
      }
    });

    localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    setDidSubmit(true);
  }, [
    formState,
    submitGetInTouchDetails
  ]);



  const isValid = useMemo(() => [
    formState.email,
    formState.name,
    formState.companyName
  ].every((v) => !!v), [formState]);

  const shouldDisableForm = useMemo(() => {
    return submitGetInTouchDetailsTuple.loading || didSubmit;
  }, [
    didSubmit,
    submitGetInTouchDetailsTuple.loading
  ]);

  return (
    <Container sx={{
      mb: 8
    }}
    >
      <Stack
        gap={2}
      >
        <Paper sx={{
          borderRadius: (theme) => theme.shape.borderRadius,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          padding: 4,
          overflow: 'hidden'
        }}
        >
          <img src={imgCustomizeCapabilities} />

          {!showForm && (
            <Box sx={{
              textAlign: 'center',
              mb: '4rem'
            }}
            >
              <Button
                variant='contained'
                size='large'
                onClick={() => {
                  setShowForm(true);
                }}
              >
                Learn More
              </Button>
            </Box>
          )}

          {showForm && (
            <Grid
              container
              rowGap={2}
              columnGap={2}
              sx={{
                justifyContent: 'center'
              }}
            >
              <Grid md={12}>
                <Stack
                  gap={2}
                  sx={{
                    textAlign: 'center'
                  }}
                >
                  {!didSubmit ? (
                    <Typography variant='activityHeader'>
                      Get in touch
                    </Typography>
                  ) : (
                    <Typography variant='activityHeader'>
                      Thank you!
                    </Typography>
                  )}

                  {!didSubmit ? (
                    <Typography variant='activitySubHeaderPrimary'>
                      Fill out the form below to request more information, and see how Sherlock can help your organization.
                    </Typography>
                  ) : (
                    <Typography variant='activitySubHeaderPrimary'>
                      We appreciate you contacting us. The team will reach out soon if there are questions.
                    </Typography>
                  )}
                </Stack>
              </Grid>

              {!didSubmit && (
                <>
                  <Grid md={4}>
                    <TextField
                      fullWidth
                      {...genFieldProps('email')}
                      disabled={shouldDisableForm}
                    />
                  </Grid>

                  <Grid md={4}>
                    <TextField
                      fullWidth
                      {...genFieldProps('name')}
                      disabled={shouldDisableForm}
                    />
                  </Grid>

                  <Grid md={4}>
                    <TextField
                      fullWidth
                      {...genFieldProps('companyName')}
                      disabled={shouldDisableForm}
                    />
                  </Grid>

                  <Grid md={4}>
                    <TextField
                      fullWidth
                      {...genFieldProps('assetsUnderManagement')}
                      disabled={shouldDisableForm}
                    />
                  </Grid>

                  <Grid md={8}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      {...genFieldProps('otherComments')}
                      disabled={shouldDisableForm}
                    />
                  </Grid>

                  <Grid md={4}>
                    {!didSubmit && (
                      <Button
                        fullWidth
                        variant='contained'
                        onClick={handleClickSubmit}
                        disabled={!isValid || shouldDisableForm}
                      >
                        {!isValid ? 'Fill out the form' : 'Submit'}
                      </Button>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          )}

        </Paper>

        <Paper sx={{
          borderRadius: (theme) => theme.shape.borderRadius,
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch'
        }}
        >
          <img src={imgCustomizeCopy} />
        </Paper>
      </Stack>
    </Container>
  );
};
